import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { cx } from '@emotion/css';

import { IEcpMapProps } from './ecp-map.models';
import { CHILD_ZOOM, LIST_ZOOM, MAP_OPTIONS } from '../../../../../../../../constants/ecp-locator';
import { useStyles } from './ecp-map.styles';
import { IEcpStore } from '../../../../../../../../rest/ecp-locator';
import { getLatLng, getMarkerIcon } from '../../../../../../../../utils/ecp-locator';
import { unwrapLatLng } from '../../../../../../../../utils/latlng/latlng';
import { setUserLocation } from '../../../../../../../../graphql/ecp-locator/ecp-locator.cache';

import { useYandexMapsScript } from '../../../../../../../../hooks/useYandexMapsScripts';
import { initializeYandexMaps, cleanupYandexMaps } from '../../../../../../../../lib/ymap';
import { MapPlaceholder } from '../../../../../../../common/map-placeholder/map-placeholder';
import { useMapStyles } from '../../../../../../../../hooks/use-map-styles/use-map-styles';
import { v2025PP18603 } from '../../../../../../../../constants/veeva';

const YANDEX_MAPS_API_KEY = process.env.REACT_APP_YANDEX_MAPS_AK || 'YOUR_API_KEY';

type LonLat = [number, number];

const toLonLat = (p: { latitude: number; longitude: number }): LonLat => [
  Number(p.longitude),
  Number(p.latitude),
];

const computeBounds = (stores: IEcpStore[]): [LonLat, LonLat] | null => {
  if (!stores?.length) return null;
  let minLat = Infinity,
    maxLat = -Infinity,
    minLon = Infinity,
    maxLon = -Infinity;
  for (const s of stores) {
    const { latitude, longitude } = unwrapLatLng(getLatLng(s));
    const lat = Number(latitude),
      lon = Number(longitude);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
    }
  }
  if (![minLat, maxLat, minLon, maxLon].every(Number.isFinite)) return null;
  const lonPad = (maxLon - minLon) * 0.05 || 0.01;
  const latPad = (maxLat - minLat) * 0.05 || 0.01;
  return [
    [minLon - lonPad, minLat - latPad],
    [maxLon + lonPad, maxLat + latPad],
  ];
};

const DEFAULT_CENTER: LonLat = [37.618423, 55.751244];

export const EcpMap: FC<IEcpMapProps> = ({
  ecpStores,
  storeDetail,
  isMapHidden,
  onSetStore,
  ...props
}): JSX.Element | null => {
  const classes = useStyles();
  const userLocation = useReactiveVar(setUserLocation);

  const { isLoaded, isError, errorMessage } = useYandexMapsScript(YANDEX_MAPS_API_KEY);
  const [mapComponents, setMapComponents] = useState<any>(null);

  const initialCenter = useMemo(() => {
    const c = unwrapLatLng(getLatLng(userLocation));
    if (Number.isFinite(c.latitude) && Number.isFinite(c.longitude)) {
      return toLonLat(c);
    }
    return DEFAULT_CENTER;
  }, [userLocation]);

  const [zoom, setZoom] = useState<number>(LIST_ZOOM);
  const [center, setCenter] = useState<LonLat>(initialCenter);
  const [boundsOnce, setBoundsOnce] = useState<[LonLat, LonLat] | null>(null);
  const [markerCode, setMarkerCode] = useState<string>('');
  const [duration, setDuration] = useState<number>(0);
  const mapRef = useRef<any>(null);
  const { mapStyles } = useMapStyles();

  const boundsAppliedRef = useRef(false);
  const prevEcpStoresRef = useRef<IEcpStore[] | undefined>(undefined);

  useEffect(() => {
    if (isLoaded && !mapComponents) {
      initializeYandexMaps()
        .then((modules) => {
          setMapComponents(modules);
        })
        .catch((error) => {
          console.error('Failed to initialize Yandex Maps:', error);
        });
    }
  }, [isLoaded, mapComponents]);

  useEffect(() => {
    return () => {
      cleanupYandexMaps();
    };
  }, []);

  useEffect(() => {
    if (boundsOnce && mapRef.current && !boundsAppliedRef.current) {
      boundsAppliedRef.current = true;
      const timer = setTimeout(() => {
        setBoundsOnce(null);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [boundsOnce]);

  useEffect(() => {
    if (ecpStores?.length) {
      const storesChanged = prevEcpStoresRef.current !== ecpStores;
      prevEcpStoresRef.current = ecpStores;

      if (storesChanged && !boundsAppliedRef.current) {
        const b = computeBounds(ecpStores);
        if (b) setBoundsOnce(b);
      }

      if (!isMapHidden) {
        setZoom(LIST_ZOOM);
      }

      setDuration(500);
      const u = unwrapLatLng(getLatLng(userLocation));
      if (Number.isFinite(u.latitude) && Number.isFinite(u.longitude)) {
        setCenter(toLonLat(u));
      }
    }
  }, [ecpStores, userLocation, isMapHidden]);

  useEffect(() => {
    if (storeDetail && storeDetail.latitude && storeDetail.longitude) {
      const u = unwrapLatLng(getLatLng(storeDetail));
      if (Number.isFinite(u.latitude) && Number.isFinite(u.longitude)) {
        setBoundsOnce(null);
        setDuration(500);
        setCenter(toLonLat(u));
        setZoom(CHILD_ZOOM);
        if (storeDetail.code && storeDetail.code !== markerCode) {
          setMarkerCode(storeDetail.code);
        }
      }
    }
  }, [storeDetail, markerCode]);

  useEffect(() => {
    if (!ecpStores?.length && !markerCode && userLocation?.latitude && userLocation?.longitude) {
      const u = unwrapLatLng(getLatLng(userLocation));
      if (Number.isFinite(u.latitude) && Number.isFinite(u.longitude)) {
        setCenter(toLonLat(u));
      }
    }
  }, [userLocation, markerCode]);

  const onMarkerClick = useCallback(
    (store: IEcpStore) => {
      const u = unwrapLatLng(getLatLng(store));
      if (!Number.isFinite(u.latitude) || !Number.isFinite(u.longitude)) return;

      if (markerCode !== store.code) setMarkerCode(store.code);

      onSetStore?.({
        ...store,
        rating: String(store.rating),
      });

      setBoundsOnce(null);
      setDuration(500);
      setCenter(toLonLat(u));
      setZoom(CHILD_ZOOM);
    },
    [markerCode, onSetStore],
  );

  const commonWrapperProps = {
    className: cx(
      classes.mapWrapper,
      { [classes.mapHidden]: isMapHidden },
      { [classes.mapWrapperStoreDetail]: !!storeDetail },
    ),
    'data-testid': 'yandex-map-wrapper' as const,
  };

  const showLoading = (!isLoaded && !isError) || !mapComponents;
  const showError =
    isError ||
    (mapComponents &&
      (!mapComponents.YMap ||
        !mapComponents.YMapDefaultSchemeLayer ||
        !mapComponents.YMapDefaultFeaturesLayer ||
        !mapComponents.YMapMarker));

  if (showLoading || showError) {
    return (
      <div {...commonWrapperProps}>
        {showLoading && <MapPlaceholder loading />}
        {showError && <MapPlaceholder error message={errorMessage || 'Failed to load map'} />}
      </div>
    );
  }

  const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapMarker, YMapControls, YMapZoomControl } = mapComponents;

  const markers = (ecpStores || [])
    .map((s) => {
      const { latitude, longitude } = unwrapLatLng(getLatLng(s));
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
      const coords: LonLat = [Number(longitude), Number(latitude)];
      const { url, width, height } = getMarkerIcon({ type: s.type, code: s.code, codeToCheck: markerCode, surgery: s.surgery });
      return (
        <YMapMarker key={s.code} coordinates={coords}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMarkerClick(s);
            }}
            title={`${s?.name ?? ''} ${s?.street ?? ''}`}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              transform: 'translate(-50%, -100%)',
              padding: 0,
            }}
            aria-label={`${s?.name ?? s?.code ?? 'store'}`}
          >
            <img src={url} width={width} height={height} alt="" draggable={false} />
          </button>
        </YMapMarker>
      );
    })
    .filter(Boolean);

  const location = boundsOnce
    ? { bounds: boundsOnce, duration: 0 }
    : { center, zoom, duration };

  return (
    <div {...commonWrapperProps}>
      <YMap ref={mapRef} location={location as any} mode="vector" {...MAP_OPTIONS} {...props}>
        <YMapDefaultSchemeLayer customization={mapStyles} />
        <YMapDefaultFeaturesLayer />
        {markers}
        <YMapControls orientation="vertical" position="right">
          <YMapZoomControl />
        </YMapControls>
      </YMap>
      <div className={classes.veevaNumber}>{v2025PP18603}</div>
    </div>
  );
};
